import { FC } from 'react';
import { useMutation } from '@apollo/client';
import {
	AppEnum,
	BanIcon, Button, ButtonAppearance, ButtonColor, LoaderBars, NotificationToast, NotificationType, alephToast,
} from '@aleph/shared-components';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import { useTranslation } from 'react-i18next';
import { SpotifyUnlinkUserGroupsFromClientDocument } from 'wb-spotify/graphql/generated';
import { AuthGraphUserGroupsOptionsQueryItems } from 'wb-spotify/graphql/authgraph/generated';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import useSpotifyClientMutator from 'wb-spotify/app/routes/superGrid/mutator/adAccount/hooks/useSpotifyAdClientMutator';

type AdAccountAccessListItemProps = {
	entity: AuthGraphUserGroupsOptionsQueryItems;
	onRemove?: () => void;
};

const AdAccountAccessListItem: FC<AdAccountAccessListItemProps> = ({ entity, onRemove }) => {
	const { t } = useTranslation();
	const { mutatingClient } = useSpotifyClientMutator();
	const isUpdate = mutatingClient?.client?.clientId;
	const user = useUserMetaData(AppEnum.SPOTIFY);

	const [
		unlinkUserGroupsFromClient,
		{ loading: isRevoking },
	] = useMutation(SpotifyUnlinkUserGroupsFromClientDocument, {
		errorPolicy: 'all',
	});

	if (!entity) {
		return null;
	}

	const onRevokeAccess = async () => {
		if (!isUpdate) {
			onRemove?.();

			return;
		}

		const { errors } = await unlinkUserGroupsFromClient({
			variables: {
				clientId:     mutatingClient?.client?.clientId ?? '',
				userGroupIds: [entity.userGroupId],
			},
		});

		if (errors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sc: action err', { action: 'revoking access' })}
				/>,
			);

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={t('re: advertiser.revoke.success')}
			/>,
		);

		onRemove?.();
	};

	return (
		<div
			className="flex h-[48px] items-center px-[8px] text-[12px] border-b border-b-gray-200"
		>
			<div className="flex-1 text-[16px] font-medium whitespace-nowrap
				  items-center text-black flex gap-[8px]"
			>
				<RedditEntityImage
					entity={entity}
				/>

				{entity.userGroupName}

			</div>
			<div className="w-[50px] flex justify-center">
				{
					user?.isAdmin && (
						isRevoking
							? (
								<LoaderBars
									width={16}
									height={16}
									className="animate-spin"
								/>
							)
							: (
								<Button
									onClick={onRevokeAccess}
									appearance={ButtonAppearance.GHOST}
									color={ButtonColor.GRAY}
									leftNode={<BanIcon
										width={16}
										height={16}
									/>}
								/>
							)
					)
				}
			</div>
		</div>
	);
};

export default AdAccountAccessListItem;
